/**
 * タイムライン項目の出自 (lineage。docs/features/video-editor-timeline-lineage-refactoring-plan.md §3)。
 *
 * 「由来 (シナリオ由来か利用者追加か)」「シナリオ上の出自 (sceneId / materialSceneId)」「複製ごとの
 * インスタンス (instanceId)」「分割片 (instanceId + 素材区間)」を別の値で持つ。移植
 * (mapper/copyProjectWithLatestScenario.ts) と継続処理 (proposal/itemSettingsContinuity.ts) の対応付けは
 * clipId ではなくこの欄で行う。clipId は「素材の参照」だけに戻す。
 *
 * - 通常モード: 1 シーン = 1 項目 = 1 インスタンス
 * - ストーリーボード: 本編素材 1 本を構成する N 区間が 1 つのインスタンス (= 区間群) で、区間は
 *   (instanceId, sceneId) で識別する。「同じ instanceId の項目」= 1 つのインスタンスを時間で分けた片
 * - 分割では両片が同じ instanceId を継承し、複製では新しい instanceId を発行する (§3.2)
 *
 * 旧データ (lineage の無い項目) は読み込み時に `inferTimelineLineage` が推定で埋め、推定したものには
 * `inferred: true` を付けて永続化する (§3.5)。
 */
import { z } from 'zod';

export const LINEAGE_SOURCES = ['scenario', 'user', 'unknown'] as const;
export type LineageSource = (typeof LINEAGE_SOURCES)[number];

export const LineageSchema = z.object({
  // 由来。'scenario' = シナリオから組み立てた項目 (とその分割片・複製)、'user' = 利用者がピッカー等で
  // 足した項目、'unknown' = 旧データで判別できないもの (§3.5)。移植の「残す / 対応付ける / 落とす」の
  // 判定はこの欄で行う
  source: z.enum(LINEAGE_SOURCES),
  // この項目が表すシナリオのシーン (scene.id)。ストーリーボードでは区間が表すシーン (span.scene.id) で、
  // 本編素材を所有するシーンではない。source が 'scenario' でも、ストーリーボードで区間が作れなかった
  // 項目 (§3.3.2) と旧ストーリーボードの区間項目 (§3.5) は持たない
  sceneId: z.string().optional(),
  // 本編素材を所有するシーン (ストーリーボード本編の項目だけ。= clip.sourceSceneId)。通常モードは省略
  materialSceneId: z.string().optional(),
  // インスタンス。組み立て時にシーンインスタンスごとに発行し (項目ごとではない)、分割では両片が同じ値を
  // 継承し、複製では新しい値を発行する。必須
  instanceId: z.string().min(1),
  // 複製元の instanceId (情報用。設定の継承や対応付けには使わない)
  duplicateOf: z.string().optional(),
  // 旧データからの推定で埋めた印 (§3.5)。確定した出自ではないので移植では保守的な規則を使う
  inferred: z.boolean().optional(),
});
export type Lineage = z.infer<typeof LineageSchema>;

/** 新しいインスタンス id。 */
export function newInstanceId(): string {
  const uuid = (globalThis.crypto as Crypto | undefined)?.randomUUID;
  return typeof uuid === 'function' ? uuid.call(globalThis.crypto) : `inst-${Math.random().toString(36).slice(2, 12)}`;
}

/** シナリオから組み立てた項目の lineage。 */
export function scenarioLineage(params: { sceneId?: string; materialSceneId?: string; instanceId: string }): Lineage {
  return {
    source: 'scenario',
    ...(params.sceneId ? { sceneId: params.sceneId } : {}),
    ...(params.materialSceneId ? { materialSceneId: params.materialSceneId } : {}),
    instanceId: params.instanceId,
  };
}

/** 利用者がピッカー等で足した項目の lineage。 */
export function userLineage(): Lineage {
  return { source: 'user', instanceId: newInstanceId() };
}

/** 複製の lineage (§3.2): 元の出自を写し、instanceId を新規発行、duplicateOf = 元の instanceId。 */
export function duplicateLineage(origin: Lineage): Lineage {
  const next: Lineage = { ...origin, instanceId: newInstanceId(), duplicateOf: origin.instanceId };
  delete next.inferred;
  return next;
}

/**
 * 結合できる lineage か (§3.2)。`sceneId` と `instanceId` が両方一致する 2 項目 (= 同じインスタンスの片) だけ。
 * ストーリーボードの別区間 (sceneId が違う) や、元と複製 (instanceId が違う) は結合しない。
 * `sceneId` を持たない項目どうし (§3.3.2・旧ストーリーボード) は instanceId 一致だけで可。
 * lineage が無い (正規化前) 項目は判定しない (ok)。
 */
export function lineageMergeCheck(a: Lineage | undefined, b: Lineage | undefined): { ok: true } | { ok: false; reason: string } {
  if (!a || !b) return { ok: true };
  if (a.instanceId !== b.instanceId) return { ok: false, reason: '複製したシーンは元のシーンと結合できません' };
  if ((a.sceneId ?? null) !== (b.sceneId ?? null)) return { ok: false, reason: '別のシーンの区間は結合できません' };
  return { ok: true };
}

/** 2 つの lineage が同じ値か。 */
export function lineagesEqual(a: Lineage | undefined, b: Lineage | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.source === b.source &&
    (a.sceneId ?? null) === (b.sceneId ?? null) &&
    (a.materialSceneId ?? null) === (b.materialSceneId ?? null) &&
    a.instanceId === b.instanceId &&
    (a.duplicateOf ?? null) === (b.duplicateOf ?? null) &&
    (a.inferred ?? false) === (b.inferred ?? false)
  );
}

// ── 旧データの移行 (§3.5) ─────────────────────────────────────────

export interface LineageInferenceItem {
  id: string;
  clipId: string;
  lineage?: Lineage;
}
export interface LineageInferenceClip {
  id: string;
  sourceSceneId?: string;
}

/**
 * lineage の無い項目に推定で埋める (読み込み時の移行。§3.5)。lineage を持つ項目は触らない。
 * 変更が無ければ同じ配列を返す。
 *
 * | 状況 | 埋め方 |
 * |---|---|
 * | 通常モードの、`clip.sourceSceneId` を持つクリップの項目 | `scenario`・`sceneId = sourceSceneId`。同じ clipId の項目は全部同じ instanceId (旧データに複製は無いので同じクリップの複数項目は必ず分割片)。inferred は付けない |
 * | ストーリーボードモードの本編クリップの項目 | `scenario`・`materialSceneId = sourceSceneId`・sceneId なし (区間が表すシーンは復元できない)。同じ clipId は同じ instanceId。`inferred: true` |
 * | ストーリーボードの本編以外 (ブランドカット・予告編静止画) | 通常モードと同じ |
 * | `sourceSceneId` の無いクリップの項目 | `unknown`・項目ごとに新しい instanceId・`inferred: true` |
 *
 * `storyboardBodyClipIds` はストーリーボードの本編クリップの id (呼び出し側が snapshot の frameMode と
 * クリップの形から決める。通常モードでは空)。
 */
export function inferTimelineLineage<T extends LineageInferenceItem>(
  timeline: T[],
  clipsById: Map<string, LineageInferenceClip>,
  storyboardBodyClipIds: ReadonlySet<string>,
  makeInstanceId: () => string = newInstanceId,
): T[] {
  if (timeline.every((item) => item.lineage)) return timeline;
  const instanceByClip = new Map<string, string>();
  const instanceFor = (clipId: string) => {
    let id = instanceByClip.get(clipId);
    if (!id) {
      id = makeInstanceId();
      instanceByClip.set(clipId, id);
    }
    return id;
  };
  return timeline.map((item) => {
    if (item.lineage) return item;
    const clip = clipsById.get(item.clipId);
    const sceneId = clip?.sourceSceneId;
    let lineage: Lineage;
    if (!sceneId) {
      lineage = { source: 'unknown', instanceId: makeInstanceId(), inferred: true };
    } else if (storyboardBodyClipIds.has(item.clipId)) {
      lineage = { source: 'scenario', materialSceneId: sceneId, instanceId: instanceFor(item.clipId), inferred: true };
    } else {
      lineage = { source: 'scenario', sceneId, instanceId: instanceFor(item.clipId) };
    }
    return { ...item, lineage };
  });
}
